// [PURPOSE]: Feature gating wrapper for premium-only content
// [WHY]: Free users see an upgrade prompt instead of premium features
// [INTEGRATION]: Wraps premium sections; triggers AuthModal for unauthenticated users
// [CREATED]: v1.0 Track 2C — Feature gating
// [S9 2026-04-16]: Inline promo-code redemption UI added to upgrade flow.
//                  Code is validated server-side before checkout so the user sees
//                  the discounted price preview without claiming a redemption slot.

import React, { useState, useEffect, useRef } from 'react';
import { Crown, Sparkles, Tag, Check, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
// [PHASE B / #024 — 2026-05-06]: Loading-aware status hook (sibling of
//   the boolean usePremiumAccess) so we can suppress the upgrade prompt
//   during the auth-init window. Pre-fix: cold refresh painted the
//   upgrade prompt for the duration of the auth fetch, then swapped to
//   children when isPremium resolved true. Visible flash for paying
//   users on every hard refresh.
import { usePremiumAccessStatus } from '../hooks/usePremiumAccess';
import AuthModal from './AuthModal';
import {
  initiatePremiumPurchase,
  validatePromoCode,
  applyPromoPreview,
} from '../utils/purchaseUtils';
// [S6]: Track premium gate impressions for conversion funnel
import { trackEvent } from '../services/analytics';
import { reportError } from '../services/errorReporter';

// [S9]: Premium unit price in cents — mirrors api/purchase-app.js. Change both together.
const PREMIUM_BASE_CENTS = 399;

// [S9]: Map server-side rejection reasons to user-friendly messages.
//       Keys MUST match the `reason` strings returned by api/validate-promo.js and api/purchase-app.js.
const PROMO_REASON_MESSAGES = {
  invalid: 'Invalid promo code.',
  inactive: 'This code is no longer active.',
  expired: 'This code has expired.',
  exhausted: 'This code is no longer available.',
  already_used: "You've already used this code.",
  race: 'This code was just claimed by another user. Try again.',
  server_error: 'Could not validate code. Please try again.',
};

// [S9]: Format a cents amount as $X.XX (US only — mirrors Stripe's currency for this product)
function formatPriceUSD(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * [PURPOSE]: Conditionally renders children or an upgrade prompt
 * [UX]: Shows locked state with clear upgrade path
 *
 * [MAY 2026 — orphan cleanup]: The `inline` prop variant (compact lock-icon
 *   button) was scaffolded in S1 (2026 Firebase Auth + Premium init) but
 *   never adopted by any call site — every consumer used the default full
 *   upgrade prompt. Branch removed during the May 2026 ship audit.
 *
 * @param {React.ReactNode} children - Premium content to gate
 * @param {string} featureName - Display name of the gated feature
 * @param {string} description - Why this feature is valuable
 * @param {boolean} requireAuth - If true, shows login prompt for unauthenticated users
 */
const PremiumGate = ({
  children,
  featureName = 'Premium Feature',
  description = 'Unlock this feature with a one-time purchase.',
  requireAuth = true,
}) => {
  const { isAuthenticated, currentUser } = useAuth();
  // [PHASE B / #024]: Pull both flags so the gate can render a neutral
  // skeleton while `loading === true`. PremiumGate must NOT paint the
  // upgrade prompt during that window — for paying users it flashes
  // "no premium" until fetchPremiumStatus resolves.
  const { hasPremium, loading: authLoading } = usePremiumAccessStatus();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const trackedRef = useRef(false);

  // [S9]: Promo-code state — all client-side, read-only against server
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoResult, setPromoResult] = useState(null); // { valid, discount_type, discount_value, code } | { valid:false, reason }
  const [promoChecking, setPromoChecking] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // [UF-024 — 2026-05-09]: Explicit Stripe-return success state. After Stripe
  //   redirects the user back to the app, the URL contains `?success=true` or
  //   `?session_id=...`. Without this banner, the gate just relies on
  //   usePremiumAccessStatus to flip silently — if the auth fetch is slow,
  //   the user briefly sees the upgrade prompt again, which feels broken.
  //   We surface a celebratory block immediately on return and clean the URL.
  const [justUnlocked, setJustUnlocked] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true' || params.get('session_id')) {
      setJustUnlocked(true);
      // Clean URL so a refresh doesn't replay the banner.
      window.history.replaceState({}, '', window.location.pathname);
      const tid = setTimeout(() => setJustUnlocked(false), 5000);
      return () => clearTimeout(tid);
    }
  }, []);

  // [S6]: Track premium gate impression once per mount (not on re-renders).
  // Fires only when gate is actually shown (user doesn't have premium).
  useEffect(() => {
    if (!hasPremium && !trackedRef.current) {
      trackedRef.current = true;
      trackEvent('premium_viewed', { feature: featureName });
    }
  }, [hasPremium, featureName]);

  // [UF-024 — 2026-05-09]: Reusable success banner shown after Stripe redirect.
  const successBanner = justUnlocked ? (
    <div
      data-testid="premium-gate-just-unlocked"
      className="bg-emerald-900/40 border border-emerald-500 rounded-lg p-4 mb-4 text-center"
    >
      <div className="text-2xl mb-1">🎉</div>
      <p className="font-bold text-emerald-300">Premium unlocked!</p>
      <p className="text-sm text-gray-300">Your purchase was successful. Welcome aboard.</p>
    </div>
  ) : null;

  // [v1.1]: Centralized premium check via usePremiumAccess hook
  if (hasPremium) {
    // [UF-024]: When the user just returned from Stripe and premium is confirmed,
    //   render ONLY the success banner above the children. Don't double-render
    //   the upgrade prompt and don't fall through to the gate.
    if (justUnlocked) {
      return (
        <>
          {successBanner}
          {children}
        </>
      );
    }
    return <>{children}</>;
  }

  // [PHASE B / #024 — 2026-05-06]: Render a low-key loading skeleton
  // while auth is still initializing. Without this, premium users see
  // the full upgrade prompt for the duration of the fetchPremiumStatus
  // round-trip, then it swaps to their content. The skeleton is small
  // and unobtrusive: a spinner-like indicator inside a placeholder card
  // matching the gate's footprint. Once `authLoading` flips to false,
  // we re-evaluate and either render children (premium) or the upgrade
  // prompt (free-tier). The free-tier user does see one frame of the
  // skeleton before the prompt — that's correct, the prompt is the
  // truthful state and the skeleton is the honest "we don't know yet."
  if (authLoading) {
    // [UF-024]: If we just returned from Stripe, show the success banner
    //   instead of the skeleton — keep the celebratory state visible until
    //   hasPremium resolves true. The banner self-dismisses at 5s.
    if (justUnlocked) {
      return <>{successBanner}</>;
    }
    return (
      <div
        data-testid="premium-gate-loading"
        className="p-6 bg-gray-800/40 rounded-xl border border-gray-700/40 text-center animate-pulse"
      >
        <div className="mb-3 mx-auto h-10 w-10 rounded-full bg-amber-700/30" />
        <div className="h-4 w-32 mx-auto rounded bg-gray-700/50 mb-2" />
        <div className="h-3 w-56 max-w-full mx-auto rounded bg-gray-700/40" />
      </div>
    );
  }

  // [S9]: Validate a promo code against the server. Does not claim a slot.
  const handleApplyPromo = async () => {
    setCheckoutError(null);
    const code = (promoInput || '').trim().toUpperCase();
    if (!code) {
      setPromoResult({ valid: false, reason: 'invalid' });
      return;
    }
    setPromoChecking(true);
    try {
      const result = await validatePromoCode(currentUser, code);
      setPromoResult(result);
      if (result && result.valid) {
        trackEvent('promo_validated', { code });
      }
    } catch (err) {
      reportError(err, { area: 'promo_validation' });
      setPromoResult({ valid: false, reason: 'server_error' });
    } finally {
      setPromoChecking(false);
    }
  };

  // [S9]: Clear the applied code so the user can enter a different one
  const handleClearPromo = () => {
    setPromoResult(null);
    setPromoInput('');
    setCheckoutError(null);
  };

  const priceCents = promoResult && promoResult.valid
    ? applyPromoPreview(PREMIUM_BASE_CENTS, promoResult)
    : PREMIUM_BASE_CENTS;
  const hasValidPromo = promoResult && promoResult.valid;

  const renderPromoSection = () => {
    // Only show the promo UI for authenticated users on the full upgrade prompt.
    // Unauth users see "Sign In" instead; promo flow requires a Firebase token.
    if (!isAuthenticated) return null;

    if (!promoOpen) {
      return (
        <button
          type="button"
          onClick={() => setPromoOpen(true)}
          className="mt-3 text-xs text-amber-400 hover:text-amber-300 transition-colors underline-offset-2 hover:underline"
        >
          Have a promo code?
        </button>
      );
    }

    if (hasValidPromo) {
      const discountLabel = promoResult.discount_type === 'percent'
        ? `${promoResult.discount_value}% off`
        : `${formatPriceUSD(promoResult.discount_value)} off`;
      // [S9 COUNTER]: show scarcity only when the server returned a remaining count.
      //               single_use/per_user_single_use codes don't surface this.
      const remaining = promoResult.uses_remaining;
      const showCounter = Number.isFinite(remaining);
      // [UX]: colour-shift the urgency line as the pool drains.
      //       <= 10: red (almost gone), <= 25: amber (going fast), otherwise emerald.
      let urgencyClass = 'text-emerald-400';
      if (showCounter && remaining <= 10) urgencyClass = 'text-red-400 font-semibold';
      else if (showCounter && remaining <= 25) urgencyClass = 'text-amber-400 font-semibold';
      return (
        <>
          <div className="mt-3 p-3 rounded-lg bg-emerald-900/20 border border-emerald-600/30 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-emerald-300 text-sm">
              <Check size={16} />
              <span>
                <span className="font-semibold">{promoResult.code}</span> applied — {discountLabel}
              </span>
            </div>
            <button
              type="button"
              onClick={handleClearPromo}
              className="text-xs text-gray-400 hover:text-gray-200 flex items-center gap-1"
              aria-label="Remove promo code"
            >
              <X size={14} />
              Remove
            </button>
          </div>
          {showCounter && (
            <p
              className={`mt-2 text-xs ${urgencyClass}`}
              data-testid="promo-remaining-counter"
              aria-live="polite"
            >
              {remaining === 0
                ? 'No spots remaining.'
                : remaining === 1
                  ? 'Only 1 spot left — grab it.'
                  : `Only ${remaining} of ${promoResult.uses_max} spots left.`}
            </p>
          )}
        </>
      );
    }

    // [fallthrough]: unused — renderPromoSection returns from the three above branches

    const errorMessage = promoResult && !promoResult.valid
      ? (PROMO_REASON_MESSAGES[promoResult.reason] || 'Could not apply this code.')
      : null;

    return (
      <div className="mt-3 text-left space-y-2">
        <label htmlFor="promo-code-input" className="flex items-center gap-1 text-xs text-amber-300">
          <Tag size={12} />
          Promo code
        </label>
        <div className="flex gap-2">
          <input
            id="promo-code-input"
            type="text"
            value={promoInput}
            onChange={(e) => {
              setPromoInput(e.target.value);
              if (promoResult) setPromoResult(null); // clear prior error as user types
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleApplyPromo(); }}
            placeholder="e.g. LAUNCH25"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={32}
            disabled={promoChecking}
            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleApplyPromo}
            disabled={promoChecking || !promoInput.trim()}
            className="px-4 py-2 bg-amber-700 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {promoChecking ? 'Checking…' : 'Apply'}
          </button>
        </div>
        {errorMessage && (
          <p className="text-xs text-red-400 flex items-center gap-1">
            <X size={12} />
            {errorMessage}
          </p>
        )}
      </div>
    );
  };

  const upgradeButtonLabel = () => {
    if (purchasing) return 'Redirecting...';
    if (hasValidPromo) return `Upgrade for ${formatPriceUSD(priceCents)}`;
    return 'Upgrade for $3.99';
  };

  // Full upgrade prompt
  return (
    <>
      {successBanner}
      <div className="p-6 bg-gradient-to-br from-amber-900/20 to-yellow-900/20 rounded-xl border border-amber-600/30 text-center">
        <div className="mb-3">
          <Crown size={40} className="text-amber-400 mx-auto" />
        </div>
        <h3 className="text-lg font-bold text-amber-300 mb-2">
          {featureName}
        </h3>
        <p className="text-sm text-gray-300 mb-4 max-w-sm mx-auto">
          {description}
        </p>

        <div className="space-y-2">
          {!isAuthenticated && requireAuth ? (
            <button
              onClick={() => setShowAuthModal(true)}
              className="w-full max-w-xs mx-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Sparkles size={16} />
              Sign In to Unlock
            </button>
          ) : (
            <button
              onClick={async () => {
                setPurchasing(true);
                setCheckoutError(null);
                try {
                  await initiatePremiumPurchase(
                    currentUser,
                    hasValidPromo ? promoResult.code : undefined
                  );
                } catch (err) {
                  reportError(err, {
                    area: 'premium_purchase_checkout',
                    extra: { hasValidPromo, promoCode: hasValidPromo ? promoResult?.code : null },
                  });
                  // [S9]: Server rejected the promo between preview and checkout — surface it
                  if (err && err.code === 'PROMO_REJECTED') {
                    setPromoResult({ valid: false, reason: err.reason });
                    setCheckoutError(PROMO_REASON_MESSAGES[err.reason] || 'Promo code could not be applied.');
                  } else {
                    setCheckoutError('Could not start checkout. Please try again.');
                  }
                  setPurchasing(false);
                }
              }}
              disabled={purchasing}
              className="w-full max-w-xs mx-auto px-6 py-2.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Crown size={16} />
              {upgradeButtonLabel()}
            </button>
          )}

          {renderPromoSection()}

          {checkoutError && (
            <p className="mt-2 text-xs text-red-400 max-w-xs mx-auto">
              {checkoutError}
            </p>
          )}
        </div>

        <p className="mt-3 text-xs text-gray-500">
          {hasValidPromo
            ? `Original $3.99 — one-time purchase. All features. All platforms.`
            : 'One-time purchase. All features. All platforms.'}
        </p>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        prompt={`Sign in to unlock ${featureName}`}
      />
    </>
  );
};

export default PremiumGate;
